import { ApolloError, useQuery } from "@apollo/client";
import React, { createContext } from "react";
import { IAdvancedEmailWizardContext, IAdvancedEmailWizardContextProviderProps } from ".";
import { DynamicTextContextEnum } from "../../enums/DynamicTextContext";
import { WizardTypeEnum } from "../../enums/WizardTypeEnum";
import { strings } from "../../localisation";
import { ErrorContext } from "../ErrorContext";
import { emailWizardInitQuery } from "./graphQL/emailWizard";
import { IEmailWizardInitData } from "./graphQL/types";

export const AdvancedEmailWizardContext = createContext<IAdvancedEmailWizardContext>({
    IsLoading: false,
    WizardType: WizardTypeEnum.Entry,
    DynamicTextContext: DynamicTextContextEnum.Entry,
    AddressTypes: {
        entryAddressTypes: [],
        fromAddresses: [],
    },
    MergeTagData: {
        mergeTags: [],
    },
    FeatureFlags: {

    }
});

const AdvancedEmailWizardContextProvider = (({ children, WizardType, DynamicTextContext }: IAdvancedEmailWizardContextProviderProps) => {

    const errorContext = React.useContext(ErrorContext);

    const initData: IEmailWizardInitData = {
        entryAddressTypes: [{
            description: strings.Label_ToAddress_Active,
            id: -1,
        }],
        fromAddresses: [],
        communication: {
            unlayerMergeTags: "{}",
            additionalDynamicAddresses: [],
        },
        featureFlag: {
            enableGraphQLDynamicTextValidationEndpoint: false,
        }
    };

    const { data, loading } = useQuery<IEmailWizardInitData>(emailWizardInitQuery, {
        variables: {
            context: DynamicTextContext,
        },
        fetchPolicy: "cache-first",
        onError: (error: ApolloError) => {
            errorContext.SetError(error);
        },
    });

    if (data) {

        // Address types
        initData.entryAddressTypes = initData.entryAddressTypes.concat(data.entryAddressTypes.slice().sort((add1, add2) => {
            if (add1.description < add2.description) {
                return -1;
            }
            if (add1.description > add2.description) {
                return 1;
            }
            return 0;
        }));

        // Add additional dynamic address types
        initData.entryAddressTypes = initData.entryAddressTypes.concat(data.communication.additionalDynamicAddresses.map((x) => ({ id: x.value, description: x.key })).sort((add1, add2) => {
            if (add1.description < add2.description) {
                return -1;
            }
            if (add1.description > add2.description) {
                return 1;
            }
            return 0;
        }));

        // From Addresses
        initData.fromAddresses = data.fromAddresses
            .filter((fromAdd) => fromAdd.fromAddress !== "")
            .slice().sort((add1, add2) => {
                if (add1.fromAddress < add2.fromAddress) {
                    return -1;
                }
                if (add1.fromAddress > add2.fromAddress) {
                    return 1;
                }
                return 0;
            });

        // Merge tags
        initData.communication.unlayerMergeTags = data.communication.unlayerMergeTags;

        // Feature Flags
        initData.featureFlag = data.featureFlag;
    }

    return (
        <AdvancedEmailWizardContext.Provider value={
            {
                IsLoading: loading,
                WizardType,
                DynamicTextContext,
                AddressTypes: {
                    entryAddressTypes: initData.entryAddressTypes,
                    fromAddresses: initData.fromAddresses,
                },
                MergeTagData: JSON.parse(initData.communication.unlayerMergeTags),
                FeatureFlags: initData.featureFlag,
            }} >
            {children}
        </AdvancedEmailWizardContext.Provider>
    );
});

export default AdvancedEmailWizardContextProvider;
